import { useEffect, useMemo } from 'react';
import {
  Dropdown,
  InlineLoading,
  RadioButton,
  RadioButtonGroup,
} from '@carbon/react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { AreaDetailSection } from 'components/area-detail-section';
import {
  AliasOption,
  aliasedOption,
  aliasedSearchText,
  aliasOptionProps,
} from 'components/alias-pills';
import { FreeTextComboBox } from 'components/free-text-combobox/free-text-combobox';
import { useAtomValue, useSetAtom } from 'jotai';
import { capitalize, debounce } from 'lodash-es';
import { ExpenseLineItemsEditor } from './expense-line-items-editor';
import { EconomicLimitsSection } from './economic-limits-section';
import { AfeSelectionSection } from './afe-selection-section';
import {
  categoriesLoadableAtom,
  areaCategoryAtom,
  areaQueryAtom,
  areaSearchLoadableAtom,
  unitTypesLoadableAtom,
  unitTypeAtom,
  unitQueryAtom,
  unitSearchLoadableAtom,
  statesLoadableAtom,
  countyQueryAtom,
  countySearchLoadableAtom,
  areaGeometriesAtom,
  fetchGeometryLoadableAtom,
} from './atoms';
import type { ExpenseAreaConfig } from '../types';
import styles from './area-details.module.scss';

interface ExpenseAreaDetailsProps {
  area: ExpenseAreaConfig;
  onChange: (patch: Partial<ExpenseAreaConfig>) => void;
}

/**
 * Editable Area Details panel. Geometry type (Area/Unit), category/area or
 * unit-type/unit pickers backed by the shared master-data lookups, alias and
 * notes (via AreaDetailSection), plus a read-only line-items table.
 * @param {ExpenseAreaDetailsProps} props - The area and change handler
 * @return {JSX.Element} The area details panel
 */
export function ExpenseAreaDetails({
  area,
  onChange,
}: ExpenseAreaDetailsProps): JSX.Element {
  const setGeometries = useSetAtom(areaGeometriesAtom);

  // Drops this area's cached geometry so the map stops drawing a stale shape.
  const clearGeometry = () =>
    setGeometries((prev) => {
      const next = { ...prev };
      delete next[area.id];
      return next;
    });

  return (
    <div className={styles.area}>
      <AreaDetailSection
        idPrefix={`area-${area.id}`}
        collapsible
        aliasValue={area.alias}
        onAliasChange={(value) => onChange({ alias: value })}
        notesValue={area.notes}
        onNotesChange={(value) => onChange({ notes: value })}
        notesPlaceholder="Notes about this area's expense assumptions..."
      >
        <RadioButtonGroup
          legendText="Geometry Type"
          name={`geometry-type-${area.id}`}
          valueSelected={area.geometryType}
          orientation="horizontal"
          onChange={(value: string | number | undefined) => {
            const next = value as ExpenseAreaConfig['geometryType'];
            onChange({
              geometryType: next,
              configurableId: 0,
              category:
                next === 'state' ? 'State' : next === 'county' ? 'County' : '',
              areaName: '',
              unitType: '',
              unitName: '',
            });
            clearGeometry();
          }}
        >
          <RadioButton
            id={`geom-area-${area.id}`}
            labelText="Area"
            value="area"
          />
          <RadioButton
            id={`geom-unit-${area.id}`}
            labelText="Unit"
            value="unit"
          />
          <RadioButton
            id={`geom-state-${area.id}`}
            labelText="State"
            value="state"
          />
          <RadioButton
            id={`geom-county-${area.id}`}
            labelText="County"
            value="county"
          />
        </RadioButtonGroup>

        {area.geometryType === 'unit' ? (
          <UnitFields area={area} onChange={onChange} />
        ) : area.geometryType === 'state' ? (
          <StateField area={area} onChange={onChange} />
        ) : area.geometryType === 'county' ? (
          <CountyField area={area} onChange={onChange} />
        ) : (
          <AreaFields area={area} onChange={onChange} />
        )}
      </AreaDetailSection>

      <Accordion defaultExpanded className={styles.accordion}>
        <AccordionSummary>
          <h5 className={styles.tableTitle}>Economic Limits</h5>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.accordionBody}>
            <EconomicLimitsSection area={area} onChange={onChange} />
          </div>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded className={styles.accordion}>
        <AccordionSummary>
          <h5 className={styles.tableTitle}>AFE Selection</h5>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.accordionBody}>
            <AfeSelectionSection area={area} onChange={onChange} />
          </div>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded className={styles.accordion}>
        <AccordionSummary>
          <h5 className={styles.tableTitle}>Expense Line Items</h5>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.accordionBody}>
            <ExpenseLineItemsEditor
              items={area.lineItems}
              onChange={(lineItems) => onChange({ lineItems })}
            />
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}

/**
 * Area-geometry fields: category dropdown (shared categories) + area typeahead
 * backed by `/queries/area_geometries`.
 * @param {ExpenseAreaDetailsProps} props - The area and change handler
 * @return {JSX.Element} The area fields
 */
function AreaFields({ area, onChange }: ExpenseAreaDetailsProps): JSX.Element {
  const categoriesState = useAtomValue(categoriesLoadableAtom);
  const areaSearchState = useAtomValue(areaSearchLoadableAtom);
  const setAreaCategory = useSetAtom(areaCategoryAtom);
  const setAreaQuery = useSetAtom(areaQueryAtom);
  const fetchGeometry = useSetAtom(fetchGeometryLoadableAtom);
  const setGeometries = useSetAtom(areaGeometriesAtom);

  // The search atoms are module-global, and the typeahead's onFocus refetch
  // below deliberately skips when results are already present — so switching
  // areas would show the previous area's results until the user typed. Exactly
  // one AreaFields is mounted at a time (scenario-form renders only the
  // selected area), so clearing on area change is enough to scope them.
  useEffect(() => {
    setAreaCategory('');
    setAreaQuery(undefined);
  }, [area.id, setAreaCategory, setAreaQuery]);

  // Drops this area's cached geometry so the map stops drawing a stale shape.
  const clearGeometry = () =>
    setGeometries((prev) => {
      const next = { ...prev };
      delete next[area.id];
      return next;
    });

  const categoryItems =
    categoriesState.state === 'hasData' && categoriesState.data
      ? categoriesState.data.categories
      : [];

  const debouncedAreaQuery = useMemo(
    () => debounce((text: string) => setAreaQuery(text), 350),
    [setAreaQuery]
  );

  useEffect(() => () => debouncedAreaQuery.cancel(), [debouncedAreaQuery]);

  const areaItems = useMemo(() => {
    if (areaSearchState.state !== 'hasData' || !areaSearchState.data) return [];
    return areaSearchState.data.results.map((r) => {
      const label =
        [r.purpose, r.name].filter(Boolean).join(' - ') || r.displayName;
      return { id: r.id, value: label, label };
    });
  }, [areaSearchState]);

  return (
    <div className={styles.formGrid}>
      {categoriesState.state === 'loading' ? (
        <InlineLoading description="Loading categories..." />
      ) : (
        <Dropdown
          id={`area-category-${area.id}`}
          titleText="Area Category (required)"
          label="Choose a category"
          size="md"
          light
          items={categoryItems}
          selectedItem={area.category || null}
          onChange={({ selectedItem }: { selectedItem: string | null }) => {
            onChange({
              category: selectedItem || '',
              areaName: '',
              configurableId: 0,
            });
            setAreaCategory(selectedItem || '');
            setAreaQuery(undefined);
            clearGeometry();
          }}
        />
      )}

      <div
        className={styles.typeaheadWrapper}
        onFocus={() => {
          if (
            area.category &&
            areaSearchState.state !== 'loading' &&
            !areaItems.length
          ) {
            setAreaCategory(area.category);
            setAreaQuery('');
          }
        }}
      >
        <FreeTextComboBox
          id={`area-selection-${area.id}`}
          titleText="Area (required)"
          placeholder="Search areas..."
          items={areaItems}
          selectedItem={
            area.areaName
              ? { value: area.areaName, label: area.areaName }
              : null
          }
          itemToString={(item) => item?.label ?? ''}
          onChange={({ selectedItem }) => {
            onChange({
              areaName: selectedItem?.value ?? '',
              configurableId: selectedItem?.id ?? 0,
            });
            if (selectedItem?.id !== undefined) {
              fetchGeometry({
                entryId: area.id,
                type: 'area',
                configurableId: selectedItem.id,
              }).catch(() => clearGeometry());
            } else {
              clearGeometry();
            }
          }}
          onInputChange={(text) => debouncedAreaQuery(text)}
        />
        {areaSearchState.state === 'loading' && (
          <InlineLoading description="" className={styles.spinner} />
        )}
      </div>
    </div>
  );
}

/**
 * Unit fields: unit-type dropdown (shared unit types) + unit-name typeahead
 * backed by `/queries/development_areas`.
 * @param {ExpenseAreaDetailsProps} props - The area and change handler
 * @return {JSX.Element} The unit fields
 */
function UnitFields({ area, onChange }: ExpenseAreaDetailsProps): JSX.Element {
  const unitTypesState = useAtomValue(unitTypesLoadableAtom);
  const unitSearchState = useAtomValue(unitSearchLoadableAtom);
  const setUnitType = useSetAtom(unitTypeAtom);
  const setUnitQuery = useSetAtom(unitQueryAtom);
  const fetchGeometry = useSetAtom(fetchGeometryLoadableAtom);
  const setGeometries = useSetAtom(areaGeometriesAtom);

  // Same module-global scoping as AreaFields above.
  useEffect(() => {
    setUnitType('');
    setUnitQuery(undefined);
  }, [area.id, setUnitType, setUnitQuery]);

  // Drops this area's cached geometry so the map stops drawing a stale shape.
  const clearGeometry = () =>
    setGeometries((prev) => {
      const next = { ...prev };
      delete next[area.id];
      return next;
    });

  const unitTypeItems =
    unitTypesState.state === 'hasData' && unitTypesState.data
      ? unitTypesState.data.unit_types
      : [];

  const debouncedUnitQuery = useMemo(
    () => debounce((text: string) => setUnitQuery(text), 350),
    [setUnitQuery]
  );

  useEffect(() => () => debouncedUnitQuery.cancel(), [debouncedUnitQuery]);

  const unitItems = useMemo(() => {
    if (unitSearchState.state !== 'hasData' || !unitSearchState.data) return [];
    return unitSearchState.data.results.map(aliasedOption);
  }, [unitSearchState]);

  return (
    <div className={styles.formGrid}>
      {unitTypesState.state === 'loading' ? (
        <InlineLoading description="Loading unit types..." />
      ) : (
        <Dropdown
          id={`unit-type-${area.id}`}
          titleText="Unit Type (required)"
          label="Choose an option"
          size="md"
          light
          items={unitTypeItems}
          selectedItem={area.unitType || null}
          itemToString={(item: string) => (item ? capitalize(item) : '')}
          onChange={({ selectedItem }: { selectedItem: string | null }) => {
            onChange({
              unitType: selectedItem || '',
              unitName: '',
              configurableId: 0,
            });
            setUnitType(selectedItem || '');
            setUnitQuery(undefined);
            clearGeometry();
          }}
        />
      )}

      <div
        className={styles.typeaheadWrapper}
        onFocus={() => {
          if (
            area.unitType &&
            unitSearchState.state !== 'loading' &&
            !unitItems.length
          ) {
            setUnitType(area.unitType);
            setUnitQuery('');
          }
        }}
      >
        <FreeTextComboBox
          id={`unit-name-${area.id}`}
          itemToElement={(item) => <AliasOption {...aliasOptionProps(item)} />}
          itemToSearchText={aliasedSearchText}
          titleText="Unit Name (required)"
          placeholder="Search units..."
          items={unitItems}
          selectedItem={
            area.unitName
              ? { value: area.unitName, label: area.unitName }
              : null
          }
          itemToString={(item) => item?.label ?? ''}
          onChange={({ selectedItem }) => {
            onChange({
              unitName: selectedItem?.value ?? '',
              configurableId: selectedItem?.id ?? 0,
            });
            if (selectedItem?.id !== undefined) {
              fetchGeometry({
                entryId: area.id,
                type: 'unit',
                configurableId: selectedItem.id,
              }).catch(() => clearGeometry());
            } else {
              clearGeometry();
            }
          }}
          onInputChange={(text) => debouncedUnitQuery(text)}
        />
        {unitSearchState.state === 'loading' && (
          <InlineLoading description="" className={styles.spinner} />
        )}
      </div>
    </div>
  );
}

/**
 * State field: typeahead backed by the shared `/states` list.
 * @param {ExpenseAreaDetailsProps} props - The area and change handler
 * @return {JSX.Element} The state field
 */
function StateField({ area, onChange }: ExpenseAreaDetailsProps): JSX.Element {
  const statesState = useAtomValue(statesLoadableAtom);

  const stateItems = useMemo(() => {
    if (statesState.state !== 'hasData' || !statesState.data) return [];
    return statesState.data.map((s) => ({
      id: s.id,
      value: s.name,
      label: s.name,
    }));
  }, [statesState]);

  return (
    <div className={styles.formGrid}>
      <div className={styles.typeaheadWrapper}>
        <FreeTextComboBox
          id={`state-selection-${area.id}`}
          titleText="State (required)"
          placeholder="Search states..."
          items={stateItems}
          selectedItem={
            area.areaName
              ? { value: area.areaName, label: area.areaName }
              : null
          }
          itemToString={(item) => item?.label ?? ''}
          onChange={({ selectedItem }) =>
            onChange({
              areaName: selectedItem?.value ?? '',
              configurableId: selectedItem?.id ?? 0,
            })
          }
        />
        {statesState.state === 'loading' && (
          <InlineLoading description="" className={styles.spinner} />
        )}
      </div>
    </div>
  );
}

/**
 * County field: typeahead backed by the shared `/queries/counties` search
 * (defaults to supported states).
 * @param {ExpenseAreaDetailsProps} props - The area and change handler
 * @return {JSX.Element} The county field
 */
function CountyField({ area, onChange }: ExpenseAreaDetailsProps): JSX.Element {
  const countySearchState = useAtomValue(countySearchLoadableAtom);
  const setCountyQuery = useSetAtom(countyQueryAtom);

  const debouncedCountyQuery = useMemo(
    () => debounce((text: string) => setCountyQuery(text), 350),
    [setCountyQuery]
  );

  useEffect(() => () => debouncedCountyQuery.cancel(), [debouncedCountyQuery]);

  // Fetch counties as soon as County is selected (on mount), not on focus.
  useEffect(() => {
    setCountyQuery('');
  }, [setCountyQuery]);

  const countyItems = useMemo(() => {
    if (countySearchState.state !== 'hasData' || !countySearchState.data) {
      return [];
    }
    return countySearchState.data.map((c) => ({
      id: c.id,
      value: c.name,
      label: c.name,
    }));
  }, [countySearchState]);

  return (
    <div className={styles.formGrid}>
      <div className={styles.typeaheadWrapper}>
        <FreeTextComboBox
          id={`county-selection-${area.id}`}
          titleText="County (required)"
          placeholder="Search counties..."
          items={countyItems}
          selectedItem={
            area.areaName
              ? { value: area.areaName, label: area.areaName }
              : null
          }
          itemToString={(item) => item?.label ?? ''}
          onChange={({ selectedItem }) =>
            onChange({
              areaName: selectedItem?.value ?? '',
              configurableId: selectedItem?.id ?? 0,
            })
          }
          onInputChange={(text) => debouncedCountyQuery(text)}
        />
        {countySearchState.state === 'loading' && (
          <InlineLoading description="" className={styles.spinner} />
        )}
      </div>
    </div>
  );
}
